/**
 * Base Service Class for MyClass School Management System
 * Provides common functionality for all module services
 */

import { supabaseAdmin } from '../supabase';

class BaseService {
  constructor(tenantId = 'tn_01') {
    this.tenantId = tenantId;
    this.actualTenantId = this.getActualTenantId(tenantId);
  }

  /**
   * Map tenant ID strings to actual UUID tenant IDs
   */
  getActualTenantId(tenantId) {
    const tenantIdMap = {
      'tn_01': 'eadcd263-a399-4261-8cbd-8fb1ce6da0e3',
      'tn_02': '1acd60af-8884-48bb-b1ea-13fbb935d3a8',
      'tn_03': 'adf83c8b-b623-4f69-b65f-25e11c2781ce'
    };
    
    const actualTenantId = tenantIdMap[tenantId];
    if (!actualTenantId) {
      console.error('Invalid tenant ID:', tenantId);
      throw new Error('Invalid tenant ID');
    }
    
    return actualTenantId;
  }

  /**
   * Generic data fetching method
   */
  async getData(table, filters = {}, options = {}) {
    try {
      let query = supabaseAdmin
        .from(table)
        .select(options.select || '*')
        .eq('tenant_id', this.actualTenantId);

      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          query = query.eq(key, value);
        }
      });

      // Apply ordering
      if (options.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending !== false });
      }

      // Apply limit
      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error(`Error fetching data from ${table}:`, error);
        throw error;
      }

      return { data: data || [], error: null };
    } catch (err) {
      console.error(`Unexpected error in getData for ${table}:`, err);
      return { data: [], error: err.message || 'Unexpected error' };
    }
  }

  /**
   * Create a new record
   */
  async createRecord(table, data) {
    try {
      const recordData = {
        ...data,
        tenant_id: this.actualTenantId,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { data: result, error } = await supabaseAdmin
        .from(table)
        .insert(recordData)
        .select()
        .single();

      if (error) {
        console.error(`Error creating record in ${table}:`, error);
        throw error;
      }

      return { data: result, error: null };
    } catch (err) {
      console.error(`Unexpected error in createRecord for ${table}:`, err);
      return { data: null, error: err.message || 'Unexpected error' };
    }
  }

  /**
   * Update an existing record
   */
  async updateRecord(table, id, data) {
    try {
      const updateData = {
        ...data,
        updated_at: new Date().toISOString()
      };

      const { data: result, error } = await supabaseAdmin
        .from(table)
        .update(updateData)
        .eq('id', id)
        .eq('tenant_id', this.actualTenantId)
        .select()
        .single();

      if (error) {
        console.error(`Error updating record in ${table}:`, error);
        throw error;
      }

      return { data: result, error: null };
    } catch (err) {
      console.error(`Unexpected error in updateRecord for ${table}:`, err);
      return { data: null, error: err.message || 'Unexpected error' };
    }
  }

  /**
   * Delete a record
   */
  async deleteRecord(table, id) {
    try {
      const { error } = await supabaseAdmin
        .from(table)
        .delete()
        .eq('id', id)
        .eq('tenant_id', this.actualTenantId);

      if (error) {
        console.error(`Error deleting record in ${table}:`, error);
        throw error;
      }

      return { data: { id }, error: null };
    } catch (err) {
      console.error(`Unexpected error in deleteRecord for ${table}:`, err);
      return { data: null, error: err.message || 'Unexpected error' };
    }
  }

  /**
   * Get records with joins
   */
  async getDataWithJoins(table, joins = [], filters = {}, options = {}) {
    try {
      let selectClause = '*';
      
      if (joins.length > 0) {
        const joinClauses = joins.map(join => {
          if (typeof join === 'string') {
            return join;
          }
          return `${join.table} (*)`;
        });
        selectClause = `*, ${joinClauses.join(', ')}`;
      }

      let query = supabaseAdmin
        .from(table)
        .select(selectClause)
        .eq('tenant_id', this.actualTenantId);

      // Apply filters
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          query = query.eq(key, value);
        }
      });

      // Apply ordering
      if (options.orderBy) {
        query = query.order(options.orderBy, { ascending: options.ascending !== false });
      }

      // Apply limit
      if (options.limit) {
        query = query.limit(options.limit);
      }

      const { data, error } = await query;

      if (error) {
        console.error(`Error fetching joined data from ${table}:`, error);
        throw error;
      }

      return { data: data || [], error: null };
    } catch (err) {
      console.error(`Unexpected error in getDataWithJoins for ${table}:`, err);
      return { data: [], error: err.message || 'Unexpected error' };
    }
  }

  /**
   * Execute raw SQL query (for complex operations)
   */
  async executeQuery(query, params = []) {
    try {
      const { data, error } = await supabaseAdmin.rpc(query, params);

      if (error) {
        console.error(`Error executing query ${query}:`, error);
        throw error;
      }

      return { data: data || [], error: null };
    } catch (err) {
      console.error(`Unexpected error in executeQuery ${query}:`, err);
      return { data: [], error: err.message || 'Unexpected error' };
    }
  }

  /**
   * Get user role and permissions
   */
  async getUserRole(userId) {
    try {
      // This would typically check user roles from a users table
      // For now, we'll return a default role
      return {
        role: 'teacher',
        permissions: ['read', 'write'],
        classMaster: true,
        subjects: ['數學', '物理']
      };
    } catch (err) {
      console.error('Error getting user role:', err);
      return {
        role: 'student',
        permissions: ['read'],
        classMaster: false,
        subjects: []
      };
    }
  }

  /**
   * Check if user has permission for action
   */
  hasPermission(userRole, action, resource) {
    // Implement permission checking logic
    // This is a simplified version
    if (userRole.role === 'admin') {
      return true;
    }
    
    if (userRole.role === 'teacher') {
      return userRole.permissions.includes(action);
    }
    
    if (userRole.role === 'student') {
      return action === 'read' && resource === 'own';
    }
    
    return false;
  }

  /**
   * Format error message for user display
   */
  formatError(error) {
    if (typeof error === 'string') {
      return error;
    }
    
    if (error.message) {
      return error.message;
    }
    
    return 'An unexpected error occurred';
  }

  /**
   * Log service activity
   */
  logActivity(action, details = {}) {
    console.log(`[${this.constructor.name}] ${action}:`, details);
    // In a real application, you might want to send this to a logging service
  }
}

export default BaseService;
